import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { CopyableId } from '@/components/app/CopyableId';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { humanizeError } from '@/lib/apiClient';
import { shortId } from '@/lib/utils';
import { useSupportTicket, useUpdateSupportStatus } from '../hooks';
import type { SupportRequestStatus } from '../types';

const ALL_STATUSES: SupportRequestStatus[] = [
  'open',
  'triaged',
  'in_progress',
  'resolved',
  'closed',
];

export function SupportDetailPage() {
  const { ticketId = '' } = useParams<{ ticketId: string }>();
  const ticketQuery = useSupportTicket(ticketId);
  const mutation = useUpdateSupportStatus(ticketId);

  const [status, setStatus] = React.useState<SupportRequestStatus>('open');
  const [note, setNote] = React.useState('');
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (!initialized && ticketQuery.data) {
      setStatus(ticketQuery.data.status);
      setNote(ticketQuery.data.adminNote ?? '');
      setInitialized(true);
    }
  }, [initialized, ticketQuery.data]);

  if (ticketQuery.isLoading) {
    return (
      <div className="space-y-4">
        <BackLink />
        <LoadingState label="Loading ticket" />
      </div>
    );
  }

  if (ticketQuery.isError || !ticketQuery.data) {
    return (
      <div className="space-y-4">
        <BackLink />
        <ErrorState
          title="Ticket failed to load"
          description={ticketQuery.error ? humanizeError(ticketQuery.error) : 'Not found.'}
          actionLabel="Retry"
          onAction={() => ticketQuery.refetch()}
        />
      </div>
    );
  }

  const t = ticketQuery.data;
  const dirty =
    status !== t.status || note.trim() !== (t.adminNote ?? '').trim();

  return (
    <div className="space-y-6">
      <BackLink />
      <PageHeader
        title={`Ticket · ${t.type.replace('_', ' ')}`}
        description={`${t.supportRequestId} · opened ${t.createdAt.slice(0, 10)}`}
        actions={<Badge variant="outline">{t.status}</Badge>}
      />

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">User description</CardTitle>
            <CardDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>User</span>
              <Link
                to={`/users/${encodeURIComponent(t.userId)}`}
                className="text-primary hover:underline"
              >
                {t.userEmail ?? t.userId}
              </Link>
              <CopyableId value={t.userId} display={shortId(t.userId, 8, 6)} label="User ID" />
              {t.appVersion ? <span>· app {t.appVersion}</span> : null}
              {t.platform ? <span>· {t.platform}</span> : null}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <pre className="whitespace-pre-wrap rounded bg-muted/30 p-3 text-sm">
              {t.description}
            </pre>
            {t.deviceInfo && (
              <div className="text-xs text-muted-foreground">
                Device: <code>{t.deviceInfo}</code>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Transition status</CardTitle>
            <CardDescription>
              Changes are written to the admin audit log alongside your optional note.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as SupportRequestStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALL_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="admin-note">Admin note (optional, 2000 chars)</Label>
              <Textarea
                id="admin-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={2000}
                rows={5}
              />
              <p className="text-xs text-muted-foreground">{note.length}/2000</p>
            </div>
            <Separator />
            <Button
              className="w-full"
              disabled={!dirty || mutation.isPending}
              onClick={() =>
                mutation.mutate({
                  status,
                  adminNote: note.trim() ? note.trim() : undefined,
                })
              }
            >
              {mutation.isPending ? 'Saving…' : 'Save transition'}
            </Button>
          </CardContent>
        </Card>
      </section>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Raw ticket</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="overflow-auto rounded bg-muted/30 p-3 font-mono text-xs">
            {JSON.stringify(t, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/support"
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="h-4 w-4" />
      Back to support
    </Link>
  );
}
